'use client';

import { useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Controller, } from 'react-hook-form';
import dayjs from '@/common/lib/dayjs';

import { CtaButton as CtaButtonType, Maybe, Overlay, SalesFlowStep } from '@/generated/contentfulTypes';
import { MergedClub as MergedClubType } from '@/common/types/api.interfaces';
import {
  CtaTypes,
} from '@/common/components/CtaButton/CtaButton.interfaces';

import {
  ExtensionVerificationForm,
} from '@/common/utils/formFields';
import { useUpdateQueryParams } from '@/widgets/BecomeAMember/hooks/useUpdateQueryParams';
import {
  mapClubsToGroupedOptions,
  validationRules,
} from './MemberVerification.utils';

import { useMemberVerification } from './MemberVerification.service';

import {
  Autocomplete,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import StepTitle from '@/common/components/StepTitle/StepTitle';
import CustomCalendarHeader from '@/common/components/CustomCalendarHeader/CustomCalendarHeader';
import CtaButton from '@/common/components/CtaButton/CtaButton';
import { Icon, Icons } from '@/common/components/Icon/Icon';

import { UrlParamKeys } from '@/common/utils/constants';

import {
  styledButtonGroup,
  styledButtonSection,
  styledDatePicker,
  styledFormWrapper,
  styledInfoButton,
  styledMembershipNumberButton,
  styledMembershipNumberField,
  styledOutlinedButton,
  styledSectionTitle,
  styledVerificationSection,
} from './MemberVerification.styled';


interface MemberVerificationProps {
  clubs: MergedClubType[];
  step: SalesFlowStep | null;
  translations: Record<string, string>;
  findClubOverlay: Maybe<Overlay>;
  findMemberNumberOverlay: Maybe<Overlay>;
  chooseNewClubCTA: Maybe<CtaButtonType>[];
  onVerificationSuccess: () => void;
  preselectedClub?: Maybe<MergedClubType>;
}

export default function MemberVerification({
  clubs,
  step,
  translations,
  findClubOverlay,
  findMemberNumberOverlay,
  chooseNewClubCTA,
  onVerificationSuccess,
  preselectedClub
}: MemberVerificationProps) {
  const datePickerRef = useRef<HTMLInputElement>(null);
  const shouldPreventCloseRef = useRef(false);
  
  const router = useRouter();
  const { searchParams, updateUrl } = useUpdateQueryParams();

  const maxValidDate = dayjs().subtract(18, 'years');

  const {
    handleBlur,
    handleContinueWithCurrentClub,
    control,
    trigger,
    pushSelectAClubAnalytics,
  } = useMemberVerification({ preselectedClub, onVerificationSuccess });

  return (
    <>
      <StepTitle title={step?.stepTitle} subtitle={step?.stepSubtitle} />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box component="form" sx={styledFormWrapper}>
          <Box sx={styledVerificationSection}>
            <Typography variant="body3" fontWeight={900} sx={styledSectionTitle}>
              {translations['extensionflow.memberverification.title.cityclub']}
            </Typography>
            <Controller
              name={ExtensionVerificationForm.HomeClub}
              control={control}
              rules={validationRules(translations).club}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  fullWidth
                  options={mapClubsToGroupedOptions(clubs)}
                  getOptionLabel={(option) => option.studioName}
                  groupBy={(option) => option.city}
                  onChange={(_, value) => {
                    const params = new URLSearchParams(searchParams.toString());
                    params.set(UrlParamKeys.StudioId, value?.clubId ?? '');
                    router.push(`${window.location.pathname}?${params.toString()}`, {
                      scroll: false,
                    });
                    if (value?.clubId && value?.studioName && value?.city) {
                      pushSelectAClubAnalytics({
                        clubId: value.clubId,
                        studioName: value.studioName,
                        city: value.city,
                      });
                    }
                    return field.onChange(value ?? null);
                  }}
                  value={field.value ?? null}
                  onBlur={() => {
                    field.onBlur();
                    handleBlur();
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        translations[
                        'extensionflow.memberverification.homeclub'
                        ]
                      }
                      onBlur={() => {
                        field.onBlur();
                        handleBlur();
                      }}
                      error={!!error}
                      helperText={error?.message || translations['extensionflow.memberverification.homeclub.helper']}
                      required
                    />
                  )}
                />
              )}
            />
            {findClubOverlay && (
              <CtaButton
                transparent
                ctaType={CtaTypes.Overlay}
                overlay={findClubOverlay}
                noArrow
                sx={styledInfoButton}
              >
                <Icon icon={Icons.InfoCircle} />
              </CtaButton>
            )}
          </Box>
          <Box sx={styledVerificationSection}>
            <Typography variant="body3" fontWeight={900} sx={styledSectionTitle}>
              {translations['extensionflow.memberverification.title.details']}
            </Typography>
            <Controller
              name={ExtensionVerificationForm.DateOfBirth}
              control={control}
              rules={validationRules(translations).dateOfBirth}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
                  onChange={(newValue) => {
                    if (newValue && dayjs.isDayjs(newValue)) {
                      const formattedDate = newValue.format('YYYY-MM-DD');
                      field.onChange(formattedDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                  onClose={() => {
                    if (shouldPreventCloseRef.current) {
                      shouldPreventCloseRef.current = false;
                      return;
                    }
                    trigger(ExtensionVerificationForm.DateOfBirth);
                  }}
                  label={translations['extensionflow.memberverification.dob']}
                  maxDate={maxValidDate}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      error: !!error,
                      helperText: error?.message || translations['extensionflow.memberverification.dob.helper'],
                      fullWidth: true,
                      required: true,
                      inputRef: datePickerRef,
                      inputProps: {
                        readOnly: true,
                        inputMode: 'none',
                      },
                      onBlur: () => {
                        field.onBlur();
                        handleBlur();
                      },
                    }
                  }}
                  slots={{ calendarHeader: CustomCalendarHeader }}
                  sx={styledDatePicker}
                />
              )}
            />
            <Controller
              name={ExtensionVerificationForm.MembershipNumber}
              rules={validationRules(translations).membershipNumber}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={
                    translations[
                    'extensionflow.memberverification.membershipnumber'
                    ]
                  }
                  fullWidth
                  error={!!error}
                  helperText={error?.message || translations['extensionflow.memberverification.membershipnumber.helper']}
                  onBlur={({ target }) => {
                    field.onBlur();
                    handleBlur();
                    trigger(ExtensionVerificationForm.MembershipNumber);
                    updateUrl({ [UrlParamKeys.MembershipNumber]: target.value });
                  }}
                  sx={styledMembershipNumberField}
                />
              )}
            />
            {findMemberNumberOverlay && (
              <CtaButton
                transparent
                ctaType={CtaTypes.Overlay}
                overlay={findMemberNumberOverlay}
                noArrow
                sx={styledMembershipNumberButton}
              >
                <Icon icon={Icons.InfoCircle} />
              </CtaButton>
            )}
          </Box>
          <Box sx={styledButtonSection}>
            <Box sx={styledButtonGroup}>
              <CtaButton
                size="large"
                fullWidth
                ctaType={CtaTypes.Button}
                onClick={handleContinueWithCurrentClub}
              >
                {
                  translations[
                  'extensionflow.memberverification.button.withcurrentclub'
                  ]
                }
              </CtaButton>
              {chooseNewClubCTA.map((cta) => cta && (
                <CtaButton
                  {...cta}
                  sx={styledOutlinedButton}
                  key={cta.sys.id}
                />))
              }
            </Box>
          </Box>
        </Box >
      </LocalizationProvider >
    </>
  );
}
